import { io } from 'socket.io-client';
import {
  REALTIME_EVENTS,
  SOCKET_PATH,
  type JoinedPayload,
  type OrderEventPayload,
  type RealtimeEvent,
} from '@cuito/types';

/**
 * Socket.IO 连接工厂（契约 §19.1 的握手参数逐字照抄 + 附录 A9 的同源代理口径）。
 *
 * **同源而不是直连 API 源**：dev 下 `window.location.origin` 是 `http://localhost:5174`，
 * 由 `packages/config/vite.base.ts` 的 `/socket.io` 代理（`ws: true`）转发到 API；
 * prod 同源部署下同一个表达式就是 API 自己。这样前端**不需要**知道 API 的地址，
 * 也不会出现「REST 走代理、WS 直连」两套地址在各环境里分叉。
 * 路径取自 `@cuito/types` 的 `SOCKET_PATH`：代理项的 key、客户端的 `path`、服务端的
 * 网关路径必须是同一个字符串，写三遍就会漂移（附录 A9 / A14：是 `/socket.io`，
 * **不是** `/api/socket.io` —— Nest 的 `setGlobalPrefix('api')` 不作用于 WS 适配器）。
 *
 * 抽成接口（`CashierSocket` + `SocketHandlers`）而不是让 hook 直接 `io()`：
 * jsdom 里连不上任何服务端，真实 `io()` 会留下永不结束的重连定时器与网络报错，
 * 组件测试因此只能断言「没崩」——而状态机的每条跃迁都要能断言。
 */
export interface CashierSocket {
  emit(event: 'join', payload: { locationId: string; lastEventAt?: string }): void;
  disconnect(): void;
}

export interface SocketHandlers {
  onConnect(): void;
  onDisconnect(reason: string): void;
  /**
   * 传输层连不上（握手失败 / `transports` 全部失败 / engine 的 timeout）。
   *
   * **必须透出来**（Task 24 验收缺陷 2 的现场）：`tryAllTransports` 在
   * `engine.io-client@6.6.7` 里**默认 false**，所以 `transports: ['websocket','polling']`
   * 在 WS 被反代/防火墙挡掉时**不会**回落到 polling，而是走这条 `connect_error`
   * （见 `engine.io-client/build/esm/socket.js` 的 `_onError`：只有
   * `tryAllTransports === true` 才 `transports.shift()` 重开）。
   * 也就是说：**传输层的回落不存在**，降级只能由 `useOrderStream` 自己按连接预算与
   * `connect_error` 来做 —— 没有这个回调，收银端就是一块「徽标说实时、永远收不到单」的砖。
   */
  onConnectError(message: string): void;
  onJoined(payload: JoinedPayload): void;
  onOrderEvent(event: OrderEvent, payload: OrderEventPayload): void;
}

/** 订单事件 = 8 个事件里去掉 `stock:changed`（它由库存页消费，接单面板不订阅） */
export type OrderEvent = Exclude<RealtimeEvent, 'stock:changed'>;

export const ORDER_EVENTS = REALTIME_EVENTS.filter(
  (event): event is OrderEvent => event !== 'stock:changed',
);

export interface CashierSocketInput {
  token: string;
  deviceId: string;
}

export function connectCashierSocket(input: CashierSocketInput, handlers: SocketHandlers): CashierSocket {
  const socket = io(window.location.origin, {
    path: SOCKET_PATH,
    auth: { token: input.token, deviceId: input.deviceId, clientType: 'CASHIER' },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
  });

  socket.on('connect', () => handlers.onConnect());
  socket.on('disconnect', (reason: string) => handlers.onDisconnect(reason));
  // `error.message` 原样带上：降级之后徽标只说「轮询模式」，真正的原因（握手被拦 / 代理 502 /
  // engine 超时）只在这里能看见，吞掉它就等于把「为什么不是实时」也一起吞掉。
  socket.on('connect_error', (error: Error) => handlers.onConnectError(error.message));
  socket.on('joined', (payload: JoinedPayload) => handlers.onJoined(payload));
  for (const event of ORDER_EVENTS) {
    socket.on(event, (payload: OrderEventPayload) => handlers.onOrderEvent(event, payload));
  }

  return {
    emit: (event, payload) => {
      socket.emit(event, payload);
    },
    disconnect: () => {
      socket.disconnect();
    },
  };
}
